import { useRef, useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { RadioGroup } from '@headlessui/react'
import { CheckCircleIcon, ArrowPathIcon } from '@heroicons/react/20/solid'
import { Room, Track, RoomEvent, createLocalTracks } from 'livekit-client';

// state management
import { useRecoilValue } from 'recoil';
import { meetingConnectionState } from '../../../data/meeting.atom';

// services
import meetingAdminService from '../../../services/meeting.admin.service';

const inputStreamOptions = [
    { id: 1, title: 'Presentator', description: 'Gebruik OBS om presentatie en videos te streamen.' },
    { id: 2, title: 'Camera & NL', description: 'Stream de camera en Nederlandse audio vanuit je browser.' },
    { id: 3, title: 'FR (enkel audio)', description: 'Stream de Franse audio vanuit je brower.' },
]

function classNames(...classes) {
    return classes.filter(Boolean).join(' ')
}

export default function AdminLivestreamView() {
    const mainPlayer = useRef();
    const subPlayer = useRef();
    const roomRef = useRef(null);
    const localVideoTrackRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const { id } = useParams();

    const [inputStream, setInputStream] = useState({ id: 4 });
    const [broadcast, setBroadcast] = useState(false);
    const [connectionStatus, setConnectionStatus] = useState('disconnected');
    const [isReconnecting, setIsReconnecting] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);

    const meetingConnection = useRecoilValue(meetingConnectionState);
    const [obsStreamData, setObsStreamData] = useState(null);
    
    // Device selection state
    const [availableDevices, setAvailableDevices] = useState({ cameras: [], microphones: [] });
    const [selectedCamera, setSelectedCamera] = useState('');
    const [selectedMicrophone, setSelectedMicrophone] = useState('');
    const [hasDevicePermissions, setHasDevicePermissions] = useState(false);
    const [remoteVideoTracks, setRemoteVideoTracks] = useState([]);

    // Get device permissions and enumerate devices
    useEffect(() => {
        requestDevicePermissions();
    }, []);

    // Auto-connect when we have meeting connection data
    useEffect(() => {
        if (meetingConnection?.url && meetingConnection?.token) {
            console.log('Meeting connection data available for admin streaming - auto-connecting');
            initializeRoom(meetingConnection.token, meetingConnection.url);
        } else {
            setConnectionStatus('disconnected');
        }
        
        // Cleanup on unmount
        return () => {
            if (roomRef.current) {
                console.log('Cleaning up admin room on unmount');
                try {
                    roomRef.current.disconnect();
                } catch (e) {
                    // Ignore cleanup errors
                }
                roomRef.current = null;
            }
            // Clean up local tracks
            if (localVideoTrackRef.current) {
                try {
                    localVideoTrackRef.current.stop();
                } catch (e) {
                    // Ignore cleanup errors
                }
                localVideoTrackRef.current = null;
            }
            if (localAudioTrackRef.current) {
                try {
                    localAudioTrackRef.current.stop();
                } catch (e) {
                    // Ignore cleanup errors
                }
                localAudioTrackRef.current = null;
            }
        };
    }, [meetingConnection]);

    const updateRemoteTracks = (room) => {
        if (!room) return;
        
        const videoTracks = [];
        room.remoteParticipants.forEach((participant) => {
            participant.videoTrackPublications.forEach((publication) => {
                if (publication.track && publication.isSubscribed) {
                    videoTracks.push({
                        track: publication.track,
                        publication,
                        participant,
                        name: publication.name || publication.trackName || 'unknown'
                    });
                }
            });
        });
        
        console.log('Admin preview: Found remote video tracks:', videoTracks.map(t => `${t.participant.identity}:${t.name}`));
        setRemoteVideoTracks(videoTracks);
    };

    const requestDevicePermissions = async () => {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
            setHasDevicePermissions(true);
            stream.getTracks().forEach(track => track.stop()); // Stop the test stream
            await enumerateDevices();
        } catch (error) {
            console.error('Failed to get device permissions:', error);
            setHasDevicePermissions(false);
        }
    };

    const enumerateDevices = async () => {
        try {
            // Use LiveKit's device enumeration if available, fallback to navigator
            let devices;
            if (Room.getLocalDevices) {
                const [cameras, microphones] = await Promise.all([
                    Room.getLocalDevices('videoinput'),
                    Room.getLocalDevices('audioinput')
                ]);
                setAvailableDevices({ cameras, microphones });
                
                // Set default selections if not already set
                if (!selectedCamera && cameras.length > 0) {
                    setSelectedCamera(cameras[0].deviceId);
                }
                if (!selectedMicrophone && microphones.length > 0) {
                    setSelectedMicrophone(microphones[0].deviceId);
                }
            } else {
                // Fallback to standard approach
                devices = await navigator.mediaDevices.enumerateDevices();
                const cameras = devices.filter(device => device.kind === 'videoinput');
                const microphones = devices.filter(device => device.kind === 'audioinput');
                
                setAvailableDevices({ cameras, microphones });
                
                // Set default selections if not already set
                if (!selectedCamera && cameras.length > 0) {
                    setSelectedCamera(cameras[0].deviceId);
                }
                if (!selectedMicrophone && microphones.length > 0) {
                    setSelectedMicrophone(microphones[0].deviceId);
                }
            }
        } catch (error) {
            console.error('Failed to enumerate devices:', error);
        }
    };

    const initializeRoom = async (token, url) => {
        // Prevent double initialization
        if (connectionStatus === 'connecting...' || connectionStatus === 'connected') {
            console.log('Room already connecting or connected, skipping initialization');
            return;
        }

        // Clean up existing room
        if (roomRef.current) {
            console.log('Cleaning up existing admin room');
            try {
                roomRef.current.disconnect();
            } catch (e) {
                console.warn('Error cleaning up room:', e);
            }
            roomRef.current = null;
        }
        
        setConnectionStatus('connecting...');
        
        try {
            // Create completely isolated room instance
            const adminRoom = new Room({
                // Use different configuration to avoid conflicts
                publishDefaults: {
                    simulcast: false,
                    videoSimulcastLayers: [],
                },
                // Disable adaptive stream to reduce complexity
                adaptiveStream: false,
            });

            // Minimal event handlers to avoid object mutation
            adminRoom.on(RoomEvent.Connected, () => {
                console.log('Admin streaming room connected');
                setConnectionStatus('connected');
                // Check for existing tracks from other participants
                updateRemoteTracks(adminRoom);
            });

            adminRoom.on(RoomEvent.Disconnected, (reason) => {
                console.log('Admin streaming room disconnected:', reason);
                setConnectionStatus('disconnected');
                setIsStreaming(false);
                setRemoteVideoTracks([]);
            });

            adminRoom.on(RoomEvent.ConnectionStateChanged, (state) => {
                console.log('Admin room connection state:', state);
                if (state === 'failed') {
                    setConnectionStatus('connection failed');
                }
            });

            // Track subscription events for previews
            adminRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                console.log(`Admin preview: Track subscribed: ${track.kind} from ${participant.identity}, name: ${publication.name}`);
                if (track.kind === Track.Kind.Video) {
                    updateRemoteTracks(adminRoom);
                }
            });

            adminRoom.on(RoomEvent.TrackUnsubscribed, (track, _, participant) => {
                console.log(`Admin preview: Track unsubscribed: ${track.kind} from ${participant.identity}`);
                if (track.kind === Track.Kind.Video) {
                    updateRemoteTracks(adminRoom);
                }
            });

            adminRoom.on(RoomEvent.ParticipantConnected, (participant) => {
                console.log(`Admin preview: Participant connected: ${participant.identity}`);
                updateRemoteTracks(adminRoom);
            });

            adminRoom.on(RoomEvent.ParticipantDisconnected, (participant) => {
                console.log(`Admin preview: Participant disconnected: ${participant.identity}`);
                updateRemoteTracks(adminRoom);
            });

            // Connect with provided credentials
            console.log('Connecting admin room to:', url);
            await adminRoom.connect(url, token);
            roomRef.current = adminRoom;
            console.log('Admin streaming room connected successfully');
            
        } catch (error) {
            console.error('Admin room connection failed:', error);
            setConnectionStatus('connection failed');
            throw error;
        }
    };

    const handleReconnect = async () => {
        if (connectionStatus === 'disconnected' || connectionStatus === 'connection failed') {
            setIsReconnecting(true);
            
            try {
                await initializeRoom();
            } finally {
                setIsReconnecting(false);
            }
        }
    };

    const startStream = async () => {
        try {
            // Connect to room first if not connected
            if (!roomRef.current && connectionStatus === 'ready') {
                console.log('Connecting to room for streaming...');
                await initializeRoom(meetingConnection.token, meetingConnection.url);
            }

            if (!roomRef.current || connectionStatus !== 'connected') {
                console.error('Room not ready for streaming');
                return;
            }

            // Start the appropriate stream type
            if (inputStream.id === 2) {
                await startCameraAndDutchStream();
            } else if (inputStream.id === 3) {
                await startFrenchAudioStream();
            }
            
            setIsStreaming(true);
            setBroadcast(true);
            console.log('Streaming started successfully');
        } catch (error) {
            console.error('Failed to start stream:', error);
            setConnectionStatus('connection failed');
        }
    };

    const stopStream = async () => {
        try {
            if (localVideoTrackRef.current && roomRef.current) {
                try {
                    await roomRef.current.localParticipant.unpublishTrack(localVideoTrackRef.current);
                } catch (e) {
                    console.warn('Failed to unpublish video track:', e);
                }
                try {
                    localVideoTrackRef.current.stop();
                } catch (e) {
                    console.warn('Failed to stop video track:', e);
                }
                localVideoTrackRef.current = null;
            }
            
            if (localAudioTrackRef.current && roomRef.current) {
                try {
                    await roomRef.current.localParticipant.unpublishTrack(localAudioTrackRef.current);
                } catch (e) {
                    console.warn('Failed to unpublish audio track:', e);
                }
                try {
                    localAudioTrackRef.current.stop();
                } catch (e) {
                    console.warn('Failed to stop audio track:', e);
                }
                localAudioTrackRef.current = null;
            }
            
            // Clear video previews
            if (mainPlayer.current) {
                mainPlayer.current.srcObject = null;
            }
            if (subPlayer.current) {
                subPlayer.current.srcObject = null;
            }
            
            setIsStreaming(false);
            setBroadcast(false);
        } catch (error) {
            console.error('Failed to stop stream:', error);
        }
    };

    const startCameraAndDutchStream = async () => {
        if (!roomRef.current || !selectedCamera || !selectedMicrophone) {
            console.error('Room not ready or devices not selected');
            return;
        }

        try {
            // Use createLocalTracks for better device handling
            const tracks = await createLocalTracks({
                audio: {
                    deviceId: selectedMicrophone,
                },
                video: {
                    deviceId: selectedCamera,
                    resolution: {
                        width: 1280,
                        height: 720,
                        frameRate: 30,
                    },
                },
            });

            const videoTrack = tracks.find(track => track.kind === Track.Kind.Video);
            const audioTrack = tracks.find(track => track.kind === Track.Kind.Audio);

            if (videoTrack) {
                await roomRef.current.localParticipant.publishTrack(videoTrack, {
                    name: 'main_camera',
                    source: Track.Source.Camera,
                });
                localVideoTrackRef.current = videoTrack;
            }

            if (audioTrack) {
                await roomRef.current.localParticipant.publishTrack(audioTrack, {
                    name: 'dutch_audio',
                    source: Track.Source.Microphone,
                });
                localAudioTrackRef.current = audioTrack;
            }
        } catch (error) {
            console.error('Failed to create tracks:', error);
            throw error;
        }
    };

    const startFrenchAudioStream = async () => {
        if (!roomRef.current || !selectedMicrophone) {
            console.error('Room not ready or microphone not selected');
            return;
        }

        try {
            // Use createLocalTracks for audio only
            const tracks = await createLocalTracks({
                audio: {
                    deviceId: selectedMicrophone,
                },
                video: false,
            });

            const audioTrack = tracks.find(track => track.kind === Track.Kind.Audio);

            if (audioTrack) {
                await roomRef.current.localParticipant.publishTrack(audioTrack, {
                    name: 'french_audio',
                    source: Track.Source.Microphone,
                });
                localAudioTrackRef.current = audioTrack;
            }
        } catch (error) {
            console.error('Failed to create audio track:', error);
            throw error;
        }
    };

    const generateStreamUrl = () => {
        meetingAdminService.getMeetingWHIPUrl(id, meetingConnection.room)
            .then(response => {
                console.log("Stream data received:", response.data);
                setObsStreamData(response.data);
            })
            .catch(error => {
                console.error("Error generating stream URL:", error);
            });
    };

    // Cleanup when switching input streams
    useEffect(() => {
        if (broadcast && isStreaming) {
            console.log('Stopping stream due to input change');
            stopStream();
        }
        
        if (inputStream.id === 1) {
            // Handle OBS-WebRTC stream
            generateStreamUrl();
        }
    }, [inputStream]);

    // Handle preview rendering based on remote tracks and local streaming state
    useEffect(() => {
        console.log('Admin preview: Updating preview with', remoteVideoTracks.length, 'remote tracks, isStreaming:', isStreaming);
        
        // Find whip_streamer track (OBS) for main player preview
        const obsTrack = remoteVideoTracks.find(t => 
            t.participant.identity === `whip_streamer_${id}`
        );
        
        // Find main_camera track (webcam) for sub player preview  
        const webcamTrack = remoteVideoTracks.find(t => 
            t.name === 'main_camera' && t.participant.identity !== `whip_streamer_${id}`
        );
        
        console.log('Admin preview tracks found:', {
            obs: obsTrack ? `${obsTrack.participant.identity}:${obsTrack.name}` : 'none',
            webcam: webcamTrack ? `${webcamTrack.participant.identity}:${webcamTrack.name}` : 'none',
            localVideo: localVideoTrackRef.current ? 'available' : 'none'
        });
        
        // Update main player (should show OBS/main_player)
        if (mainPlayer.current) {
            if (obsTrack) {
                const stream = new MediaStream([obsTrack.track.mediaStreamTrack]);
                mainPlayer.current.srcObject = stream;
                mainPlayer.current.play().catch(e => console.warn('Main player autoplay prevented:', e));
            } else if (!isStreaming) {
                // Only clear if not streaming (to avoid clearing during stream transitions)
                mainPlayer.current.srcObject = null;
            }
        }
        
        // Update sub player (should show webcam/main_camera) 
        if (subPlayer.current) {
            if (isStreaming && localVideoTrackRef.current && inputStream.id === 2) {
                // Show local webcam when admin is streaming camera
                console.log('Admin preview: Showing local webcam in subplayer');
                const stream = new MediaStream([localVideoTrackRef.current.mediaStreamTrack]);
                subPlayer.current.srcObject = stream;
                subPlayer.current.play().catch(e => console.warn('Sub player autoplay prevented:', e));
            } else if (webcamTrack) {
                // Show remote webcam from other participants
                console.log('Admin preview: Showing remote webcam in subplayer');
                const stream = new MediaStream([webcamTrack.track.mediaStreamTrack]);
                subPlayer.current.srcObject = stream;
                subPlayer.current.play().catch(e => console.warn('Sub player autoplay prevented:', e));
            } else {
                // Clear when no webcam is available
                console.log('Admin preview: No webcam available, clearing subplayer');
                subPlayer.current.srcObject = null;
            }
        }
        
    }, [remoteVideoTracks, isStreaming, inputStream.id]);

    return (
        <main className="flex h-screen">
            <div className="py-6 w-2/3">
                <div className="flex justify-between items-start mx-auto px-4 sm:px-6 md:px-8">
                    <div>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Livestream</h1>
                        </div>
                        <div>
                            <p>Beheer de livestreams. Selecteer de inputs. </p>
                        </div>
                    </div>
                    <div className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full transition-all duration-500 ${
                            isStreaming ? 'bg-blue-500 shadow-lg shadow-blue-500/50 animate-pulse' :
                            connectionStatus === 'connected' ? 'bg-green-500 shadow-lg shadow-green-500/50' :
                            connectionStatus === 'connecting...' ? 'bg-yellow-500 animate-pulse shadow-lg shadow-yellow-500/50' :
                            connectionStatus === 'connection failed' ? 'bg-red-500 shadow-lg shadow-red-500/50' :
                            connectionStatus === 'disconnected' ? 'bg-orange-500 animate-pulse shadow-lg shadow-orange-500/50' :
                            'bg-gray-400'
                        }`}></div>
                        <span className={`text-sm font-medium transition-colors duration-300 ${
                            isStreaming ? 'text-blue-700' :
                            connectionStatus === 'connected' ? 'text-green-700' :
                            connectionStatus === 'connecting...' ? 'text-yellow-700' :
                            connectionStatus === 'connection failed' ? 'text-red-700' :
                            connectionStatus === 'disconnected' ? 'text-orange-700' :
                            'text-gray-600'
                        }`}>
                            {isStreaming ? 'streaming' : connectionStatus}
                        </span>
                        
                        {/* Reconnect button for disconnected/failed states */}
                        {(connectionStatus === 'disconnected' || connectionStatus === 'connection failed') && (
                            <button
                                onClick={handleReconnect}
                                disabled={isReconnecting}
                                className="ml-2 p-1.5 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 rounded-md transition-colors"
                                title="Reconnect"
                            >
                                <ArrowPathIcon 
                                    className={`w-4 h-4 transition-transform duration-200 ${
                                        isReconnecting ? 'animate-spin' : 'hover:rotate-180'
                                    }`} 
                                />
                            </button>
                        )}
                    </div>
                </div>

                <div className="px-6">
                    <RadioGroup value={inputStream} onChange={setInputStream}>
                        <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-3 sm:gap-x-4">
                            {inputStreamOptions.map((inputStream) => (
                                <RadioGroup.Option
                                    key={inputStream.id}
                                    value={inputStream}
                                    className={({ checked, active }) =>
                                        classNames(
                                            checked ? 'border-transparent' : 'border-gray-300',
                                            active ? 'border-indigo-500 ring-2 ring-indigo-500' : '',
                                            'relative flex cursor-pointer rounded-lg border bg-white p-4 shadow-sm focus:outline-none'
                                        )
                                    }
                                >
                                    {({ checked, active }) => (
                                        <>
                                            <span className="flex flex-1">
                                                <span className="flex flex-col">
                                                    <RadioGroup.Label as="span" className="block text-sm font-medium text-gray-900">
                                                        {inputStream.title}
                                                    </RadioGroup.Label>
                                                    <RadioGroup.Description as="span" className="mt-1 flex items-center text-sm text-gray-500">
                                                        {inputStream.description}
                                                    </RadioGroup.Description>
                                                </span>
                                            </span>
                                            <CheckCircleIcon
                                                className={classNames(!checked ? 'invisible' : '', 'h-5 w-5 text-indigo-600')}
                                                aria-hidden="true"
                                            />
                                            <span
                                                className={classNames(
                                                    active ? 'border' : 'border-2',
                                                    checked ? 'border-indigo-500' : 'border-transparent',
                                                    'pointer-events-none absolute -inset-px rounded-lg'
                                                )}
                                                aria-hidden="true"
                                            />
                                        </>
                                    )}
                                </RadioGroup.Option>
                            ))}
                        </div>
                    </RadioGroup>

                    {inputStream.id === 1 ? (
                        <div className="mt-5 relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6">
                            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md text-center">
                                <p className="text-sm font-medium text-green-800">OBS-WebRTC fork niet meer nodig</p>
                                <p className="text-sm text-green-700 mt-1">Je kunt nu direct streamen vanuit gewone OBS.</p>
                            </div>
                            <p className="text-sm font-medium text-gray-900 mb-4">Connectiegegevens hoofdspeler (presentator)</p>
                            
                            {obsStreamData ? (
                                <div className="space-y-4">
                                    <div className="bg-gray-50 p-4 rounded-lg">
                                        <h4 className="font-medium text-gray-900 mb-3">OBS Stream Instellingen:</h4>
                                        <div className="space-y-3">
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Stream URL:</label>
                                                <div className="flex items-center space-x-2">
                                                    <input 
                                                        type="text" 
                                                        value={obsStreamData.url}
                                                        readOnly
                                                        className="flex-1 block rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                                                    />
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(obsStreamData.url)}
                                                        className="px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200"
                                                    >
                                                        Kopieer
                                                    </button>
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-sm font-medium text-gray-700 mb-1">Stream Key:</label>
                                                <div className="flex items-center space-x-2">
                                                    <input 
                                                        type="text" 
                                                        value={obsStreamData.stream_key}
                                                        readOnly
                                                        className="flex-1 block rounded-md border-gray-300 bg-gray-100 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm font-mono"
                                                    />
                                                    <button
                                                        onClick={() => navigator.clipboard.writeText(obsStreamData.stream_key)}
                                                        className="px-3 py-2 text-sm bg-indigo-100 text-indigo-700 rounded-md hover:bg-indigo-200"
                                                    >
                                                        Kopieer
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="text-xs text-gray-600 bg-blue-50 p-3 rounded">
                                                <p><strong>Room:</strong> {obsStreamData.room_name}</p>
                                                <p><strong>Participant:</strong> {obsStreamData.participant_identity}</p>
                                                <p><strong>Ingress ID:</strong> {obsStreamData.ingress_id}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center py-4">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500 mx-auto"></div>
                                    <p className="text-sm text-gray-600 mt-2">Genereren van OBS connectiegegevens...</p>
                                </div>
                            )}
                        </div>
                    ) : inputStream.id === 2 ? (
                        <div className="mt-5 relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6">
                            <p className="text-sm font-medium text-gray-900 mb-3">Camera en nederlandse audio.</p>
                            
                            {!hasDevicePermissions ? (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <p className="text-sm text-yellow-800">Camera en microfoon permissies zijn vereist om te streamen.</p>
                                    <button 
                                        onClick={requestDevicePermissions}
                                        className="mt-2 text-sm text-yellow-900 underline hover:text-yellow-700"
                                    >
                                        Permissies opnieuw aanvragen
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Camera:</label>
                                        <select 
                                            value={selectedCamera}
                                            onChange={(e) => setSelectedCamera(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            disabled={isStreaming}
                                        >
                                            {availableDevices.cameras.map(camera => (
                                                <option key={camera.deviceId} value={camera.deviceId}>
                                                    {camera.label || `Camera ${camera.deviceId.slice(0, 8)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Microfoon:</label>
                                        <select 
                                            value={selectedMicrophone}
                                            onChange={(e) => setSelectedMicrophone(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            disabled={isStreaming}
                                        >
                                            {availableDevices.microphones.map(mic => (
                                                <option key={mic.deviceId} value={mic.deviceId}>
                                                    {mic.label || `Microfoon ${mic.deviceId.slice(0, 8)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                            
                            <div className="mt-5">
                                {broadcast ? (
                                    <button
                                        onClick={stopStream}
                                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
                                    >
                                        STOP STREAM
                                    </button>
                                ) : (
                                    <button
                                        onClick={startStream}
                                        disabled={(connectionStatus !== 'ready' && connectionStatus !== 'connected') || !hasDevicePermissions || !selectedCamera || !selectedMicrophone}
                                        className={`inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                                            ((connectionStatus === 'ready' || connectionStatus === 'connected') && hasDevicePermissions && selectedCamera && selectedMicrophone) 
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500' 
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        START STREAM
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : inputStream.id === 3 ? (
                        <div className="mt-5 relative block w-full rounded-lg border-2 border-dashed border-gray-300 p-6">
                            <p className="text-sm font-medium text-gray-900 mb-3">Franse (getolkte) audio.</p>
                            
                            {!hasDevicePermissions ? (
                                <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                                    <p className="text-sm text-yellow-800">Microfoon permissies zijn vereist om te streamen.</p>
                                    <button 
                                        onClick={requestDevicePermissions}
                                        className="mt-2 text-sm text-yellow-900 underline hover:text-yellow-700"
                                    >
                                        Permissies opnieuw aanvragen
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-4 mb-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Microfoon:</label>
                                        <select 
                                            value={selectedMicrophone}
                                            onChange={(e) => setSelectedMicrophone(e.target.value)}
                                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                            disabled={isStreaming}
                                        >
                                            {availableDevices.microphones.map(mic => (
                                                <option key={mic.deviceId} value={mic.deviceId}>
                                                    {mic.label || `Microfoon ${mic.deviceId.slice(0, 8)}`}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            )}
                            
                            <div className="mt-5">
                                {broadcast ? (
                                    <button
                                        onClick={stopStream}
                                        className="inline-flex items-center justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 font-medium text-red-700 hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 sm:text-sm"
                                    >
                                        STOP STREAM
                                    </button>
                                ) : (
                                    <button
                                        onClick={startStream}
                                        disabled={(connectionStatus !== 'ready' && connectionStatus !== 'connected') || !hasDevicePermissions || !selectedMicrophone}
                                        className={`inline-flex items-center justify-center rounded-md border border-transparent px-4 py-2 font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${
                                            ((connectionStatus === 'ready' || connectionStatus === 'connected') && hasDevicePermissions && selectedMicrophone) 
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200 focus:ring-green-500' 
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                    >
                                        START STREAM
                                    </button>
                                )}
                            </div>
                        </div>
                    ) : null}
                </div>
            </div>
            
            <div className="py-6 w-1/3 bg-gray-100 h-max-screen">
                <div className="flex mx-auto px-4 sm:px-6 md:px-8">
                    <div>
                        <div>
                            <h1 className="text-2xl font-semibold text-gray-900">Voorbeelden</h1>
                        </div>
                        <div>
                            <p>Hieronder zie je de 2 voorbeeldstreams. Dit is exact hoe de gebruiker ze te zien krijgt.</p>
                        </div>
                    </div>
                </div>
                <div className="mx-auto px-4 sm:px-6 md:px-8 pt-10">
                    <h2 className='text-xl font-semibold text-gray-700'>Hoofdspeler:</h2>
                    <video className="w-full pt-3 bg-gray-200" ref={mainPlayer} muted></video>
                    <p className="text-sm text-gray-600 mt-2">
                        {remoteVideoTracks.find(t => t.participant.identity === `whip_streamer_${id}` || t.publication.source === Track.Source.ScreenShare) 
                            ? 'OBS/Presentatie stream wordt getoond' 
                            : 'Wachten op OBS/Presentatie stream...'}
                    </p>
                </div>
                <div className="mx-auto px-4 sm:px-6 md:px-8 pt-12">
                    <h2 className='text-xl font-semibold text-gray-700'>Subspeler:</h2>
                    <video className="w-full pt-3 bg-gray-200" ref={subPlayer} muted></video>
                    <p className="text-sm text-gray-600 mt-2">
                        {(isStreaming && localVideoTrackRef.current && inputStream.id === 2) || 
                         remoteVideoTracks.find(t => t.name === 'main_camera' || (t.publication.source === Track.Source.Camera && t.participant.identity !== `whip_streamer_${id}`))
                            ? 'Webcam stream wordt getoond'
                            : 'Wachten op webcam stream...'}
                    </p>
                </div>
            </div>
        </main>
    )
}